/**
 * Organization mutation actions (switch, resend invitation, cancel, update).
 * Extracted from useOrganizations for the 150-line rule.
 */

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  queryKeys,
  useOrgResendInvitation,
  useOrgCancelInvitation,
  useOrgUpdateOrganization,
} from "@/application/queries";
import { useSwitchOrganization } from "./useSwitchOrganization";
import { useCurrentOrganizationId } from "./useCurrentOrganizationId";

export const useOrganizationActions = () => {
  const queryClient = useQueryClient();
  const orgId = useCurrentOrganizationId();

  const resendInvitationMutation = useOrgResendInvitation(orgId);
  const cancelInvitationMutation = useOrgCancelInvitation(orgId);
  const switchOrganizationMutation = useSwitchOrganization();
  const updateOrganizationMutation = useOrgUpdateOrganization(orgId);

  const loadUserOrganizations = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.organization.userOrganizations(),
    });
  }, [queryClient]);

  const loadOrganizationDetails = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.organization.details(orgId),
    });
  }, [queryClient, orgId]);

  const loadOrganizationUsers = useCallback(() => {
    queryClient.refetchQueries({
      queryKey: queryKeys.organization.users(orgId),
    });
  }, [queryClient, orgId]);

  const loadOrganizationInvitations = useCallback(() => {
    queryClient.refetchQueries({
      queryKey: queryKeys.organization.invitations(orgId),
    });
  }, [queryClient, orgId]);

  const loadOrganizationTemplates = useCallback(() => {
    queryClient.refetchQueries({
      queryKey: queryKeys.organization.templates(orgId),
    });
  }, [queryClient, orgId]);

  const dispatchSwitchOrganization = useCallback(
    async (organizationId: string | number) => {
      try {
        return await switchOrganizationMutation.mutateAsync(organizationId);
      } catch {
        return;
      }
    },
    [switchOrganizationMutation],
  );

  const dispatchResendInvitation = useCallback(
    async (invitationUuid: string) => {
      try {
        return await resendInvitationMutation.mutateAsync(invitationUuid);
      } catch {
        return;
      }
    },
    [resendInvitationMutation],
  );

  const dispatchCancelInvitation = useCallback(
    async (invitationUuid: string) => {
      try {
        return await cancelInvitationMutation.mutateAsync(invitationUuid);
      } catch {
        return;
      }
    },
    [cancelInvitationMutation],
  );

  const dispatchUpdateOrganization = useCallback(
    async (data: Parameters<typeof updateOrganizationMutation.mutateAsync>[0]) => {
      try {
        return await updateOrganizationMutation.mutateAsync(data);
      } catch {
        return;
      }
    },
    [updateOrganizationMutation],
  );

  return {
    isUpdatingOrganization: updateOrganizationMutation.isPending,
    loadUserOrganizations,
    loadOrganizationDetails,
    loadOrganizationUsers,
    loadOrganizationInvitations,
    loadOrganizationTemplates,
    switchOrganization: dispatchSwitchOrganization,
    resendInvitation: dispatchResendInvitation,
    cancelInvitation: dispatchCancelInvitation,
    updateOrganization: dispatchUpdateOrganization,
  };
};
